/**
 * Breadcrumbs Component
 *
 * A responsive breadcrumb navigation component that uses CSS to show/hide items based on overflow.
 *
 * @example
 * <gmx-breadcrumbs>
 *   <nav aria-label="Breadcrumb">
 *     <ol>
 *       <li><a href="/">Home</a></li>
 *       <li><a href="/products">Products</a></li>
 *       <li><a href="/products/vehicles">Vehicles</a></li>
 *     </ol>
 *   </nav>
 * </gmx-breadcrumbs>
 */

interface BreadcrumbItem {
	element: HTMLLIElement
	href: string
}

export class GmxBreadcrumbs extends HTMLElement {
	private breadcrumbItems: BreadcrumbItem[] = []
	private nav: HTMLElement | null = null
	private list: HTMLOListElement | null = null
	private overflowPlaceholder: HTMLLIElement | null = null
	private resizeObserver: ResizeObserver | null = null
	private debounceTimer: number | null = null
	private rafId: number | null = null
	private isChecking = false

	connectedCallback() {
		this.init()
	}

	disconnectedCallback() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
		}
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer)
		}
		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
		}
	}

	private init(): void {
		this.nav = this.querySelector('nav')
		this.list = this.querySelector('ol')

		if (!this.nav || !this.list) {
			console.warn('GmxBreadcrumbs: Expected nav with ol element')
			return
		}

		const listItems = Array.from(this.list.querySelectorAll(':scope > li'))

		this.breadcrumbItems = listItems.map((li) => {
			const anchor = li.querySelector('a')
			return {
				element: li as HTMLLIElement,
				href: anchor?.getAttribute('href') || 'javascript:void(0)',
			}
		})

		if (this.list) {
			this.list.setAttribute('data-item-count', this.breadcrumbItems.length.toString())
		}

		this.setupStructure()

		if (this.breadcrumbItems.length > 3) {
			this.createOverflowPlaceholder()
		}

		this.setupResizeObserver()

		requestAnimationFrame(() => {
			this.checkOverflow()
		})
	}

	private setupStructure(): void {
		if (!this.list) return

		if (!this.list.classList.contains('breadcrumbs-container')) {
			this.list.classList.add('breadcrumbs-container')
		}

		this.breadcrumbItems.forEach((item, index) => {
			const li = item.element

			if (!li.classList.contains('breadcrumb-item')) {
				li.classList.add('breadcrumb-item')
			}

			li.setAttribute('data-index', index.toString())

			const anchor = li.querySelector('a')
			if (anchor && !anchor.parentElement?.classList.contains('breadcrumb-link-wrapper')) {
				const span = document.createElement('span')
				span.className = 'breadcrumb-link-wrapper'
				anchor.parentNode?.insertBefore(span, anchor)
				span.appendChild(anchor)
			}

			if (index === this.breadcrumbItems.length - 1 && anchor) {
				anchor.setAttribute('aria-current', 'page')
			}
		})
	}

	private createOverflowPlaceholder(): void {
		if (!this.list) return

		const li = document.createElement('li')
		li.className = 'breadcrumb-item overflow-placeholder-item'
		li.setAttribute('data-placeholder', 'true')

		const span = document.createElement('span')
		span.className = 'breadcrumb-link-wrapper'

		const anchor = document.createElement('a')
		anchor.href = 'javascript:void(0)'
		anchor.textContent = '...'
		anchor.setAttribute('aria-label', 'Show hidden breadcrumbs')

		span.appendChild(anchor)
		li.appendChild(span)

		if (this.breadcrumbItems.length > 0) {
			const firstItem = this.breadcrumbItems[0].element
			firstItem.after(li)
			this.overflowPlaceholder = li
		}
	}

	private setupResizeObserver(): void {
		if (!this.list) return

		this.resizeObserver = new ResizeObserver(() => {
			this.debouncedCheckOverflow()
		})

		this.resizeObserver.observe(this.list)
	}

	private debouncedCheckOverflow(): void {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer)
		}

		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
		}

		if (this.isChecking) {
			return
		}

		this.debounceTimer = window.setTimeout(() => {
			this.rafId = requestAnimationFrame(() => {
				this.checkOverflow()
			})
		}, 150)
	}

	private checkOverflow(): void {
		if (!this.list || this.isChecking) return

		// Don't apply overflow logic for very short breadcrumbs
		if (this.breadcrumbItems.length <= 3) {
			this.list.classList.remove('has-overflow', 'collapse-penultimate')
			return
		}

		this.isChecking = true

		const mode = this.getResponsiveMode()
		this.setMode(mode)
		this.list.classList.remove('has-overflow', 'collapse-penultimate')

		const containerWidth = this.list.offsetWidth
		const contentWidth = this.measureContentWidth()

		if (contentWidth > containerWidth) {
			if (mode === 'mobile') {
				this.applyMobileLayout()
			} else {
				this.applyOverflowLayout()
			}
		}

		this.isChecking = false
	}

	private getResponsiveMode(): 'mobile' | 'tablet' | 'desktop' {
		const width = window.innerWidth
		if (width <= 480) return 'mobile'
		if (width <= 900) return 'tablet'
		return 'desktop'
	}

	private measureContentWidth(): number {
		this.list.classList.add('is-measuring')
		void this.list.offsetWidth // Force reflow

		let totalWidth = 0
		this.breadcrumbItems.forEach((item) => {
			totalWidth += item.element.offsetWidth
		})

		this.list.classList.remove('is-measuring')
		return totalWidth
	}

	private applyMobileLayout(): void {
		if (!this.list) return

		this.list.classList.add('has-overflow')

		if (this.overflowPlaceholder && this.breadcrumbItems.length > 1) {
			const targetItem = this.breadcrumbItems[this.breadcrumbItems.length - 2]
			const anchor = this.overflowPlaceholder.querySelector('a')
			if (anchor && targetItem) {
				anchor.href = targetItem.href
			}
		}
	}

	private applyOverflowLayout(): void {
		if (!this.list) return

		// Check if we should hide the penultimate item to give more space to first/last
		const shouldCollapsePenultimate = this.shouldCollapsePenultimate()

		this.list.classList.add('has-overflow')
		if (shouldCollapsePenultimate) {
			this.list.classList.add('collapse-penultimate')
		}

		// Update overflow placeholder link to point to the hidden item
		this.updateOverflowPlaceholderLink()
	}

	private updateOverflowPlaceholderLink(): void {
		if (!this.overflowPlaceholder || this.breadcrumbItems.length <= 2) return

		const targetItem = this.breadcrumbItems[this.breadcrumbItems.length - 3]
		const anchor = this.overflowPlaceholder.querySelector('a')
		if (anchor && targetItem) {
			anchor.href = targetItem.href
		}
	}

	private shouldCollapsePenultimate(): boolean {
		if (!this.list || this.breadcrumbItems.length < 3 || !this.overflowPlaceholder) {
			return false
		}

		const containerWidth = this.list.offsetWidth
		const SEPARATOR_WIDTH = 16
		const ITEM_PADDING = 24

		// Get text content from anchors
		const getText = (index: number): string => {
			const anchor = this.breadcrumbItems[index]?.element.querySelector('a')
			return anchor?.textContent || ''
		}

		// Calculate total width needed for: first + "..." + penultimate + last
		const totalWidth =
			this.measureTextWidth(getText(0)) + ITEM_PADDING + SEPARATOR_WIDTH +
			(this.overflowPlaceholder.offsetWidth || 40) + SEPARATOR_WIDTH +
			this.measureTextWidth(getText(this.breadcrumbItems.length - 2)) + ITEM_PADDING + SEPARATOR_WIDTH +
			this.measureTextWidth(getText(this.breadcrumbItems.length - 1)) + ITEM_PADDING

		return totalWidth > containerWidth
	}

	private measureTextWidth(text: string): number {
		const span = document.createElement('span')
		span.style.cssText = 'position: absolute; visibility: hidden; white-space: nowrap;'
		span.textContent = text

		// Copy font styles from first breadcrumb item
		const firstItem = this.breadcrumbItems[0]?.element
		if (firstItem) {
			const style = window.getComputedStyle(firstItem)
			span.style.font = style.font
		}

		document.body.appendChild(span)
		const width = span.offsetWidth
		document.body.removeChild(span)

		return width
	}

	private setMode(mode: 'mobile' | 'tablet' | 'desktop'): void {
		if (!this.list) return
		this.list.classList.remove('mode-mobile', 'mode-tablet', 'mode-desktop')
		this.list.classList.add(`mode-${mode}`)
	}
}

if (!customElements.get('gmx-breadcrumbs')) {
	customElements.define('gmx-breadcrumbs', GmxBreadcrumbs)
}